import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArtistsAPI } from '../api/ArtistsAPI';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';
import SpotifyLinkReview from '../components/artists/SpotifyLinkReview';

import './dashboard/components/Marker-UI.css';
import '../components/artists/Artist-UI.css';
import PageTitle from '../components/PageTitle';

export default function ArtistManagement() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', instagram: '', spotifyId: '' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' });
  const [reviewOpen, setReviewOpen] = useState(false);

  const fetchArtists = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ArtistsAPI.getAll();
      setArtists(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Artists:', err);
      setError('Konnte Artists nicht laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArtists(); }, [fetchArtists]);

  // Artists the Spotify review can still link: no ID and not marked "kein Spotify" by an
  // earlier review. The dialog takes a snapshot when it opens.
  const unlinked = useMemo(
    () => artists.filter((a) => !String(a.spotifyId || '').trim() && !a.noSpotify),
    [artists],
  );
  const handleUpdated = (updated) =>
    setArtists((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));

  const resetNoSpotify = async (artist) => {
    try {
      setError(null);
      const updated = await ArtistsAPI.update(artist.id, {
        name: artist.name, instagram: artist.instagram ?? null, spotifyId: null, noSpotify: false,
      });
      handleUpdated(updated && typeof updated === 'object' ? updated : { ...artist, noSpotify: false });
    } catch (err) {
      console.error('Markierung konnte nicht zurückgesetzt werden:', err);
      setError('Konnte die Markierung „kein Spotify“ nicht zurücksetzen.');
    }
  };

  const handleEditClick = (artist) => {
    setEditingId(artist.id);
    setEditData({ name: artist.name || '', instagram: artist.instagram || '', spotifyId: artist.spotifyId || '' });
  };

  const handleCancelEdit = () => { setEditingId(null); };

  const handleSave = async (id) => {
    try {
      setSaving(true);
      setError(null);
      const updated = await ArtistsAPI.update(id, editData);
      setArtists((prev) => prev.map((a) => (a.id === id ? updated : a)));
      setEditingId(null);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Konnte Artist nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (artist) =>
    setConfirmDelete({ open: true, id: artist.id, name: artist.name });

  const handleDelete = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: '' });
    try {
      setError(null);
      await ArtistsAPI.delete(id);
      setArtists((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setError('Konnte Artist nicht löschen.');
    }
  };

  const filtered = artists.filter((a) => {
    const q = search.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.instagram?.toLowerCase().includes(q) ||
      a.spotifyId?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <div className="page-header">
        <PageTitle title="Künstler" description="Künstler aus den Line-ups, mit ihren Spotify- und Instagram-Profilen. Verknüpfe sie, damit die App Bilder und Links zeigt." />
      </div>

      <div className="art-toolbar">
        <span className="art-count">{filtered.length} Künstler</span>
        <input
          className="art-search"
          type="search"
          placeholder="Name, Instagram oder Spotify-ID suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn-add btn-add--green" type="button" onClick={fetchArtists} disabled={loading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 3v6h6"/>
          </svg>
          Neu laden
        </button>
        <button className="btn-add" type="button" onClick={() => setReviewOpen(true)} disabled={loading || unlinked.length === 0}
          title={unlinked.length === 0 ? 'Alle Künstler haben eine Spotify-ID' : `${unlinked.length} Künstler ohne Spotify-ID auf Spotify suchen`}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true">
            <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.622.622 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 0 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.13-9.965-1.166a.78.78 0 0 1-.973-.519.781.781 0 0 1 .519-.972c3.632-1.102 8.147-.568 11.234 1.328a.78.78 0 0 1 .257 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.793c3.532-1.072 9.404-.865 13.115 1.338a.937.937 0 0 1-.955 1.612z"/>
          </svg>
          Spotify-Abgleich{unlinked.length > 0 && <span className="art-krudos">{unlinked.length}</span>}
        </button>
      </div>

      {error && <p className="art-error">{error}</p>}

      {loading ? (
        <div className="art-spinner" aria-label="Lädt Artists" />
      ) : filtered.length === 0 ? (
        <div className="art-empty">Keine Künstler gefunden.</div>
      ) : (
        <div className="art-card">
          <table className="art-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Instagram</th>
                <th>Spotify ID</th>
                <th>Krudos</th>
                <th style={{ textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((artist) =>
                editingId === artist.id ? (
                  <tr key={artist.id} className="art-edit-row">
                    <td colSpan={4}>
                      <div className="art-edit-form">
                        <input
                          className="art-edit-input"
                          placeholder="Name"
                          value={editData.name}
                          onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                        />
                        <input
                          className="art-edit-input"
                          placeholder="Instagram"
                          value={editData.instagram}
                          onChange={(e) => setEditData((p) => ({ ...p, instagram: e.target.value }))}
                        />
                        <input
                          className="art-edit-input"
                          placeholder="Spotify ID"
                          value={editData.spotifyId}
                          onChange={(e) => setEditData((p) => ({ ...p, spotifyId: e.target.value }))}
                          style={{ minWidth: 200 }}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="art-actions">
                        <button className="art-btn art-btn--save" onClick={() => handleSave(artist.id)} disabled={saving}>
                          {saving ? 'Speichern…' : 'Speichern'}
                        </button>
                        <button className="art-btn" onClick={handleCancelEdit} disabled={saving}>
                          Abbrechen
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={artist.id}>
                    <td className="art-name">{artist.name || '—'}</td>
                    <td>
                      {artist.instagram ? (
                        <a
                          className="art-ig-link"
                          href={`https://instagram.com/${artist.instagram}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          @{artist.instagram}
                        </a>
                      ) : (
                        <span className="art-ig">—</span>
                      )}
                    </td>
                    <td>
                      {artist.spotifyId ? (
                        <span className="art-spotify">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11">
                            <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.622.622 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 0 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.13-9.965-1.166a.78.78 0 0 1-.973-.519.781.781 0 0 1 .519-.972c3.632-1.102 8.147-.568 11.234 1.328a.78.78 0 0 1 .257 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.793c3.532-1.072 9.404-.865 13.115 1.338a.937.937 0 0 1-.955 1.612z"/>
                          </svg>
                          {artist.spotifyId}
                        </span>
                      ) : artist.noSpotify ? (
                        <span className="art-nospotify" title="Im Spotify-Abgleich als „passt nicht“ markiert">
                          Kein Spotify
                          <button type="button" className="art-nospotify-reset" onClick={() => resetNoSpotify(artist)}>
                            Zurücksetzen
                          </button>
                        </span>
                      ) : (
                        <span className="art-ig">—</span>
                      )}
                    </td>
                    <td>
                      <span className="art-krudos">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {artist.krudosCount ?? 0}
                      </span>
                    </td>
                    <td>
                      <div className="art-actions">
                        <button className="art-btn" onClick={() => handleEditClick(artist)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          Bearbeiten
                        </button>
                        <button className="art-btn art-btn--danger" onClick={() => openDelete(artist)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {reviewOpen && (
        <SpotifyLinkReview artists={unlinked} onClose={() => setReviewOpen(false)} onUpdated={handleUpdated} />
      )}

      <ConfirmDeleteModal
        open={confirmDelete.open}
        title={`"${confirmDelete.name}" löschen?`}
        message="Der Artist wird dauerhaft gelöscht und kann nicht wiederhergestellt werden."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null, name: '' })}
      />
    </div>
  );
}
